import { createContext, useContext, useReducer, useCallback, useMemo, ReactNode } from 'react'
import {
  DreamityPlanResDto,
  DreamityPlanSaveReqDto,
  DreamityPlanModifyReqDto,
} from '../types/plan.types'
import { dreamityPlannerRepository } from '@/services/dreamityPlannerRepository'
import { scheduleDebouncedPushDreamitySnapshot } from '@/services/dreamitySyncService'

interface PlanState {
  // goalId별 계획 목록 저장
  plansMap: Record<string, DreamityPlanResDto[]>
  selectedPlan: DreamityPlanResDto | null
  loading: boolean
  error: string | null
}

type PlanAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_GOAL_PLANS'; payload: { goalId: string; plans: DreamityPlanResDto[] } }
  | { type: 'SET_SELECTED_PLAN'; payload: DreamityPlanResDto | null }
  | { type: 'UPDATE_PLAN'; payload: { goalId: string; plan: DreamityPlanResDto } }
  | { type: 'DELETE_PLAN'; payload: { goalId: string; planId: string } }

interface PlanContextType extends PlanState {
  plans: DreamityPlanResDto[] // 호환성을 위해 flat list 제공
  fetchPlans: (dreamId: string, goalId: string) => Promise<DreamityPlanResDto[]>
  fetchPlanById: (dreamId: string, goalId: string, planId: string) => Promise<DreamityPlanResDto | null>
  createPlan: (dreamId: string, plan: DreamityPlanSaveReqDto) => Promise<DreamityPlanResDto[]>
  updatePlan: (dreamId: string, planId: string, plan: DreamityPlanModifyReqDto) => Promise<void>
  deletePlan: (dreamId: string, planId: string) => Promise<void>
  selectPlan: (plan: DreamityPlanResDto | null) => void
}

const PlanContext = createContext<PlanContextType | undefined>(undefined)

const initialState: PlanState = {
  plansMap: {},
  selectedPlan: null,
  loading: false,
  error: null,
}

function planReducer(state: PlanState, action: PlanAction): PlanState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_GOAL_PLANS':
      return {
        ...state,
        plansMap: {
          ...state.plansMap,
          [action.payload.goalId]: action.payload.plans,
        },
      }
    case 'SET_SELECTED_PLAN':
      return { ...state, selectedPlan: action.payload }
    case 'UPDATE_PLAN': {
      const { goalId, plan } = action.payload
      const goalPlans = state.plansMap[goalId] || []
      return {
        ...state,
        plansMap: {
          ...state.plansMap,
          [goalId]: goalPlans.map((p) => (p.planId === plan.planId ? plan : p)),
        },
        selectedPlan: state.selectedPlan?.planId === plan.planId ? plan : state.selectedPlan,
      }
    }
    case 'DELETE_PLAN': {
      const { goalId, planId } = action.payload
      const goalPlans = state.plansMap[goalId] || []
      return {
        ...state,
        plansMap: {
          ...state.plansMap,
          [goalId]: goalPlans.filter((p) => p.planId !== planId),
        },
        selectedPlan: state.selectedPlan?.planId === planId ? null : state.selectedPlan,
      }
    }
    default:
      return state
  }
}

interface PlanProviderProps {
  children: ReactNode
}

export function PlanProvider({ children }: PlanProviderProps) {
  const [state, dispatch] = useReducer(planReducer, initialState)

  const fetchPlans = useCallback(async (dreamId: string, goalId: string): Promise<DreamityPlanResDto[]> => {
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const plans = dreamityPlannerRepository.listPlans(dreamId, goalId)
      dispatch({ type: 'SET_GOAL_PLANS', payload: { goalId, plans } })
      return plans
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }, [])

  const fetchPlanById = useCallback(async (dreamId: string, goalId: string, planId: string) => {
    try {
      const plan = dreamityPlannerRepository.getPlan(planId)
      if (plan && plan.dreamId === dreamId && plan.goalId === goalId) {
        dispatch({ type: 'SET_SELECTED_PLAN', payload: plan })
        dispatch({ type: 'UPDATE_PLAN', payload: { goalId, plan } })
        return plan
      }
      return null
    } catch (error) {
      return null
    }
  }, [])

  const createPlan = async (dreamId: string, plan: DreamityPlanSaveReqDto): Promise<DreamityPlanResDto[]> => {
    try {
      dreamityPlannerRepository.createPlan(dreamId, { ...plan, dreamId })
      const plans = dreamityPlannerRepository.listPlans(dreamId, plan.goalId)
      dispatch({ type: 'SET_GOAL_PLANS', payload: { goalId: plan.goalId, plans } })
      scheduleDebouncedPushDreamitySnapshot()
      return plans
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const updatePlan = async (_dreamId: string, planId: string, plan: DreamityPlanModifyReqDto) => {
    try {
      dreamityPlannerRepository.updatePlan(planId, plan)
      const updated = dreamityPlannerRepository.getPlan(planId)
      if (updated && updated.goalId) {
        dreamityPlannerRepository.syncActionFrequencyForPlan(planId, updated.frequency)
        dispatch({ type: 'UPDATE_PLAN', payload: { goalId: updated.goalId, plan: updated } })
      }
      scheduleDebouncedPushDreamitySnapshot()
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    }
  }

  const deletePlan = async (_dreamId: string, planId: string) => {
    try {
      const plan = dreamityPlannerRepository.getPlan(planId)
      if (plan && plan.goalId) {
        dreamityPlannerRepository.deletePlan(planId)
        dispatch({ type: 'DELETE_PLAN', payload: { goalId: plan.goalId, planId } })
        scheduleDebouncedPushDreamitySnapshot()
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const selectPlan = (plan: DreamityPlanResDto | null) => {
    dispatch({ type: 'SET_SELECTED_PLAN', payload: plan })
  }

  // 모든 계획을 하나의 리스트로 합쳐서 반환
  const allPlans = useMemo(() => {
    return Object.values(state.plansMap).flat()
  }, [state.plansMap])

  return (
    <PlanContext.Provider
      value={{
        ...state,
        plans: allPlans,
        fetchPlans,
        fetchPlanById,
        createPlan,
        updatePlan,
        deletePlan,
        selectPlan,
      }}
    >
      {children}
    </PlanContext.Provider>
  )
}

export function usePlanContext() {
  const context = useContext(PlanContext)
  if (context === undefined) {
    throw new Error('usePlan must be used within a PlanProvider')
  }
  return context
}
